/**
 * Audit action catalogue — the single source of truth for:
 *   • the Module → Action cascade on both audit surfaces (Super Admin
 *     cross-tenant log + Company Admin tenant log),
 *   • the coloured module badge shown in each row, and
 *   • the full-form British-English labels for every stored action key.
 *
 * Each module's `value` is the dot-namespace prefix the backend writes
 * (everything before the first dot in `Audit.write({ action })`). Each
 * action's `value` is the exact stored key (e.g. `tenant.plan_changed`).
 * Keep this in sync with the writers — an entry with no writer simply
 * shows an empty feed when selected.
 *
 * `tone` keys into TONE_CLASSES below. The full literal class strings
 * live there (not built dynamically) so Tailwind's content scanner can
 * see every badge colour at build time.
 */
export const AUDIT_MODULES = [
  {
    value: 'tenant',
    label: 'Company',
    tone: 'indigo',
    actions: [
      { value: 'tenant.updated', label: 'Company details updated' },
      { value: 'tenant.plan_changed', label: 'Plan changed' },
      { value: 'tenant.features_changed', label: 'Features changed' },
      // Platform-only: the delete writes the row with tenantId: null (the
      // company no longer exists), so it can never appear in a Company
      // Admin's tenant-scoped feed — only the Super Admin cross-tenant log.
      // `platformOnly` drops it from the Company Admin action dropdown.
      { value: 'tenant.deleted', label: 'Company deleted', platformOnly: true },
    ],
  },
  {
    value: 'user',
    label: 'Users',
    tone: 'sky',
    actions: [
      { value: 'user.invited', label: 'Member invited' },
      // An EXISTING user accepting an invite to an additional company. A
      // brand-new user's first-time claim is recorded as
      // `auth.invite_claimed` (Authentication module) instead — see
      // acceptJoinInvite in auth.service.js (DF_057 / bug 13434).
      { value: 'user.joined_company', label: 'Joined company' },
      { value: 'user.role_changed', label: 'Member role changed' },
      { value: 'user.suspended', label: 'Member suspended' },
      { value: 'user.reactivated', label: 'Member reactivated' },
      { value: 'user.password_reset', label: 'Member password reset' },
      { value: 'user.exit_handover', label: 'Exit handover completed' },
      { value: 'user.invite_resent', label: 'Invitation resent' },
      {
        value: 'user.password_reset_forced',
        label: 'Password reset forced',
      },
      { value: 'user.deleted', label: 'User deleted' },
      // A person saving their own display & accessibility preferences
      // (My Preferences / header theme & text-size controls). Metadata
      // lists only the keys that changed, as { from, to }.
      { value: 'user.preferences_updated', label: 'Display preferences changed' },
    ],
  },
  {
    value: 'workspace',
    label: 'Workspaces',
    tone: 'emerald',
    actions: [
      { value: 'workspace.created', label: 'Workspace created' },
      { value: 'workspace.updated', label: 'Workspace updated' },
      { value: 'workspace.archived', label: 'Workspace archived' },
      { value: 'workspace.deleted', label: 'Workspace deleted' },
      { value: 'workspace.member_added', label: 'Member added to workspace' },
      {
        value: 'workspace.member_removed',
        label: 'Member removed from workspace',
      },
    ],
  },
  {
    value: 'space',
    label: 'Spaces',
    tone: 'teal',
    actions: [
      { value: 'space.created', label: 'Space created' },
      { value: 'space.updated', label: 'Space updated' },
      { value: 'space.deleted', label: 'Space deleted' },
      { value: 'space.member_added', label: 'Member added to space' },
      { value: 'space.member_removed', label: 'Member removed from space' },
      { value: 'list.created', label: 'List created' },
    ],
  },
  {
    value: 'tasks',
    label: 'Tasks',
    tone: 'amber',
    actions: [
      { value: 'tasks.created', label: 'Task created' },
      { value: 'tasks.status_changed', label: 'Task status changed' },
      { value: 'tasks.deleted', label: 'Task deleted' },
      { value: 'tasks.verification_submitted', label: 'Task submitted for verification' },
      { value: 'tasks.verification_approved', label: 'Task verification approved' },
      { value: 'tasks.verification_rejected', label: 'Task verification rejected' },
      // Still emitted by the CSV-import path, but hidden from the Action
      // filter dropdown. The label is kept so any existing rows keep
      // reading as "Tasks imported from CSV" (not an auto-humanised key).
      {
        value: 'tasks.csv_import',
        label: 'Tasks imported from CSV',
        hidden: true,
      },
    ],
  },
  {
    value: 'sop',
    label: 'SOPs',
    tone: 'lime',
    actions: [
      { value: 'sop.uploaded', label: 'SOP uploaded' },
      { value: 'sop.approved', label: 'SOP approved' },
      { value: 'sop.deleted', label: 'SOP deleted' },
      { value: 'sop.integration_updated', label: 'SOP integration updated' },
    ],
  },
  {
    // Files & Links governance trail. Company Admins can see private
    // files, so every time one actually OPENS someone else's private
    // file (metadata view, download, or share-link creation) the access
    // is recorded here — that audit trail is what makes the capability
    // accountable rather than silent.
    value: 'file',
    label: 'Files',
    tone: 'amber',
    actions: [
      {
        value: 'file.private_accessed_by_admin',
        label: 'Private file opened by Company Admin',
      },
    ],
  },
  {
    value: 'custom_field',
    label: 'Custom fields',
    tone: 'teal',
    actions: [
      { value: 'custom_field.created', label: 'Custom field created' },
      { value: 'custom_field.updated', label: 'Custom field updated' },
      { value: 'custom_field.deleted', label: 'Custom field deleted' },
    ],
  },
  {
    value: 'api_key',
    label: 'API keys',
    tone: 'cyan',
    actions: [
      { value: 'api_key.created', label: 'API key created' },
      { value: 'api_key.revoked', label: 'API key revoked' },
      { value: 'api_key.deleted', label: 'API key deleted' },
    ],
  },
  {
    value: 'auth',
    label: 'Authentication',
    tone: 'rose',
    description:
      'Password-reset and invitation events only. Sign-ins and sign-outs are on the Login Activity page.',
    actions: [
      {
        value: 'auth.password_reset_requested',
        label: 'Password reset requested',
      },
      {
        value: 'auth.password_reset_completed',
        label: 'Password reset completed',
      },
      // Still emitted by the invite-accept flow, but hidden from the Action
      // filter dropdown by request. The label is kept so any existing rows
      // keep reading as "Invitation claimed" (not an auto-humanised key).
      {
        value: 'auth.invite_claimed',
        label: 'Invitation claimed',
        hidden: true,
      },
    ],
  },
  {
    value: 'support',
    label: 'Support tickets',
    tone: 'violet',
    actions: [
      { value: 'support.ticket_created', label: 'Support ticket created' },
      { value: 'support.ticket_updated', label: 'Support ticket updated' },
    ],
  },
  {
    value: 'notification',
    label: 'Notifications',
    tone: 'yellow',
    actions: [
      { value: 'notification.read', label: 'Notification marked as read' },
      {
        value: 'notification.bulk_read',
        label: 'All notifications marked as read',
      },
      { value: 'notification.deleted', label: 'Notification deleted' },
    ],
  },
  {
    value: 'gamification',
    label: 'Gamification',
    tone: 'amber',
    actions: [
      { value: 'gamification.settings_updated', label: 'Gamification settings updated' },
      { value: 'gamification.badge_created', label: 'Badge created' },
      { value: 'gamification.badge_updated', label: 'Badge updated' },
      { value: 'gamification.badge_deleted', label: 'Badge deleted' },
    ],
  },
  {
    value: 'plan',
    label: 'Plans',
    tone: 'emerald',
    actions: [
      // Plan CATALOGUE management (create/edit/delete a plan) is a platform
      // action written WITHOUT a tenantId, so it never appears in a Company
      // Admin's feed — mark it platformOnly to hide from the CA dropdown.
      // The two events below DO stamp tenantId (a tenant activating a free
      // plan / hitting a plan limit), so they stay visible to the CA.
      { value: 'plan.created', label: 'Plan created', platformOnly: true },
      { value: 'plan.updated', label: 'Plan updated', platformOnly: true },
      { value: 'plan.deleted', label: 'Plan deleted', platformOnly: true },
      { value: 'plan.activated_free', label: 'Free plan activated' },
      { value: 'plan.limit_reached', label: 'Plan limit reached' },
    ],
  },
  {
    value: 'coupon',
    label: 'Coupons',
    tone: 'fuchsia',
    actions: [
      { value: 'coupon.created', label: 'Coupon created' },
      { value: 'coupon.updated', label: 'Coupon updated' },
      { value: 'coupon.deleted', label: 'Coupon deleted' },
    ],
  },
  {
    value: 'lead',
    label: 'Leads',
    tone: 'orange',
    actions: [
      { value: 'lead.created', label: 'Lead created' },
      { value: 'lead.updated', label: 'Lead updated' },
      { value: 'lead.deleted', label: 'Lead deleted' },
    ],
  },
  {
    value: 'payment',
    label: 'Payments',
    tone: 'green',
    actions: [{ value: 'payment.completed', label: 'Payment completed' }],
  },
  {
    value: 'email_template',
    label: 'Email templates',
    tone: 'blue',
    actions: [
      { value: 'email_template.created', label: 'Email template created' },
      { value: 'email_template.updated', label: 'Email template updated' },
      { value: 'email_template.deleted', label: 'Email template deleted' },
      { value: 'email_template.test_sent', label: 'Test email sent' },
    ],
  },
  {
    value: 'email_assignment',
    label: 'Email assignments',
    tone: 'blue',
    actions: [
      { value: 'email_assignment.saved', label: 'Email assignment saved' },
    ],
  },
  {
    value: 'email_settings',
    label: 'Email settings',
    tone: 'blue',
    actions: [{ value: 'email_settings.updated', label: 'Email settings updated' }],
  },
  {
    value: 'payment_settings',
    label: 'Payment settings',
    tone: 'green',
    actions: [
      { value: 'payment_settings.updated', label: 'Payment settings updated' },
    ],
  },
  {
    // Super Admin impersonation lifecycle. These rows are written with
    // tenantId: null, so they appear ONLY on the platform cross-tenant log —
    // a Company Admin's tenant-scoped feed can never surface them. Both
    // actions are platformOnly (kept out of the Company Admin action
    // dropdown) and `impersonation` is deliberately absent from
    // CA_MODULE_PREFIXES, so the module never shows to a Company Admin.
    value: 'impersonation',
    label: 'Impersonation',
    tone: 'rose',
    description:
      'Super Admin impersonation sessions — visible only on this platform log.',
    actions: [
      {
        value: 'impersonation.started',
        label: 'Impersonation started',
        platformOnly: true,
      },
      {
        value: 'impersonation.ended',
        label: 'Impersonation ended',
        platformOnly: true,
      },
    ],
  },
  {
    value: 'platform',
    label: 'Platform settings',
    tone: 'slate',
    actions: [
      { value: 'platform.setting_set', label: 'Setting set' },
      { value: 'platform.setting_updated', label: 'Setting updated' },
      { value: 'platform.setting_reset', label: 'Setting reset' },
      { value: 'platform.logo_cleared', label: 'Logo cleared' },
      { value: 'platform.maintenance_set', label: 'Maintenance turned on' },
      {
        value: 'platform.maintenance_updated',
        label: 'Maintenance settings updated',
      },
    ],
  },
]

/** Tenant-scoped subset — only the modules whose writers stamp a
 *  tenantId, so the Company Admin log never offers a Module that would
 *  always return an empty feed for a single company.
 *
 *  `workspace` and `custom_field` are intentionally EXCLUDED from the
 *  Company Admin filter: TaskMagix is single-Workspace (the panel hides
 *  Workspace management — single-workspace de-emphasis, see AdminLayout),
 *  and the "Custom fields" module was dropped from the CA audit filter by
 *  request. Both stay in the full AUDIT_MODULES so their labels still render
 *  on any historical rows and the Super Admin cross-tenant log keeps them. */
export const CA_MODULE_PREFIXES = [
  'tenant',
  'user',
  'space',
  'tasks',
  'sop',
  'api_key',
  'auth',
  'support',
  'notification',
  'gamification',
  // Plans: only `plan.activated_free` + `plan.limit_reached` are tenant-
  // scoped (the catalogue actions are `platformOnly`, hidden from the CA).
  'plan',
]

/** Full literal badge classes per tone. Kept as complete strings (no
 *  `bg-${tone}-50` interpolation) so Tailwind v4's content scanner
 *  emits every colour. The chip reads as a light tile with dark text —
 *  self-contained, so it stays legible on both light and dark cards. */
const TONE_CLASSES = {
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  sky: 'bg-sky-50 text-sky-700 border-sky-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  lime: 'bg-lime-50 text-lime-700 border-lime-200',
  teal: 'bg-teal-50 text-teal-700 border-teal-200',
  cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  violet: 'bg-violet-50 text-violet-700 border-violet-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  fuchsia: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
}

const MODULE_BY_PREFIX = new Map(AUDIT_MODULES.map((m) => [m.value, m]))
const ACTION_BY_KEY = new Map(
  AUDIT_MODULES.flatMap((m) =>
    m.actions.map((a) => [
      a.value,
      { ...a, tone: m.tone, moduleLabel: m.label },
    ]),
  ),
)

/** Prefix (everything before the first dot) of a dotted action key. */
function prefixOf(action) {
  return String(action ?? '').split('.')[0] || ''
}

/** "email_assignment.saved" → "Email Assignment Saved". Title-cases
 *  the dotted/underscored key — the readable fallback for any action
 *  not yet curated in AUDIT_MODULES above. */
function humaniseKey(key) {
  if (!key || typeof key !== 'string') return 'Audit entry'
  return key
    .split(/[._]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** Module label for the row badge — "Company", "Plans", … Falls back
 *  to a title-cased prefix for any uncatalogued module. */
export function moduleLabel(action) {
  const m = MODULE_BY_PREFIX.get(prefixOf(action))
  return m ? m.label : humaniseKey(prefixOf(action))
}

/** Optional one-line scope note for a module — shown under the audit
 *  filters when that module is selected, so users understand exactly what
 *  it covers (and what it doesn't). Empty string when none is defined. */
export function moduleDescription(prefix) {
  return MODULE_BY_PREFIX.get(prefix)?.description ?? ''
}

/** Full-form action label for the Description column. Falls back to a
 *  humanised key so a brand-new action still reads cleanly. */
export function actionLabel(action) {
  const a = ACTION_BY_KEY.get(action)
  return a ? a.label : humaniseKey(action)
}

/** Resolve a free-text search term to the audit-action KEYS whose displayed
 *  label (curated), raw key, or humanised key contains it — case-insensitive.
 *
 *  The audit `action` is stored as a dot-namespaced KEY (`tasks.created`) but
 *  the table shows a curated LABEL (e.g. "Task created"); the label is
 *  presentation-only and never persisted. So a user searching by what they SEE
 *  ("Task created") can't be matched by a plain regex on the stored key. The
 *  Audit Log page calls this to translate the term into matching keys and pass
 *  them to the backend, which then matches `action ∈ keys` — making Action (and
 *  the label portion of Description) searchable. `companyAdmin` drops
 *  platform-only actions that can never appear in a tenant feed. */
export function actionKeysMatching(term, { companyAdmin = false } = {}) {
  const q = String(term ?? '').trim().toLowerCase()
  if (!q) return []
  const keys = []
  for (const m of AUDIT_MODULES) {
    for (const a of m.actions) {
      if (companyAdmin && a.platformOnly) continue
      const label = String(a.label ?? '').toLowerCase()
      const key = String(a.value ?? '').toLowerCase()
      const humanKey = humaniseKey(a.value).toLowerCase()
      if (label.includes(q) || key.includes(q) || humanKey.includes(q)) {
        keys.push(a.value)
      }
    }
  }
  return keys
}

/** Tailwind badge classes for a given action, keyed by its module. */
export function actionToneClasses(action) {
  const m = MODULE_BY_PREFIX.get(prefixOf(action))
  return TONE_CLASSES[m?.tone] ?? TONE_CLASSES.slate
}

/** Compact plain-English description for a table row — the full-form
 *  action label, with the affected resource appended when one is
 *  recorded so the line says more than the badge alone. The detail
 *  modal carries the full metadata for anyone who needs it. */
export function describeAuditRow(row) {
  const label = actionLabel(row?.action)
  if (row?.resourceType) {
    const noun = humaniseKey(row.resourceType)
    const id = row.resourceId ? ` ${String(row.resourceId).slice(0, 8)}` : ''
    return `${label} · ${noun}${id}`
  }
  return label
}

/** Module dropdown options. Pass a list of prefixes to restrict the
 *  list (the Company Admin log passes CA_MODULE_PREFIXES); omit it for
 *  the full cross-tenant catalogue. The leading "All modules" entry is
 *  included so the dropdown can clear back to no filter. */
export function moduleOptions(prefixes) {
  const mods = prefixes
    ? AUDIT_MODULES.filter((m) => prefixes.includes(m.value))
    : AUDIT_MODULES
  return [
    { value: '', label: 'All modules' },
    ...mods.map((m) => ({ value: m.value, label: m.label })),
  ]
}

/** Action dropdown options for a chosen module. Returns an empty list
 *  when no module is selected so the caller can show the disabled
 *  "Select module first" placeholder. */
export function actionOptions(modulePrefix, { companyAdmin = false } = {}) {
  const m = MODULE_BY_PREFIX.get(modulePrefix)
  if (!m) return []
  return [
    { value: '', label: 'All actions' },
    ...m.actions
      .filter((a) => !a.hidden)
      // `platformOnly` actions (e.g. `tenant.deleted`, written with a null
      // tenantId) can never appear in a Company Admin's tenant-scoped feed,
      // so drop them from that dropdown; the Super Admin log keeps them.
      .filter((a) => !(companyAdmin && a.platformOnly))
      .map((a) => ({ value: a.value, label: a.label })),
  ]
}
